import React, { useEffect, useState } from 'react';
import { usePos } from '../context/PosContext';
import { useRestaurant } from '../hooks/useRestaurant';
import OrderPanel from '../components/OrderPanel';
import './Sections.css';
import './Restaurant.css';

// Refresh our table lock well within LOCK_STALE_MS so it never ages out mid-edit.
const LOCK_HEARTBEAT_MS = 30000;

// Table floor plan / table billing. Mirrors tables_bill_fragment.xml: a grid of
// tables (free vs occupied) where tapping a table opens a running order that
// fires KOTs to the kitchen and is finally settled into a bill.
const Tables: React.FC = () => {
  const { formatMoney, isOn, choice } = usePos();
  const rest = useRestaurant();
  const [selected, setSelected] = useState<string | null>(null);
  const [newTable, setNewTable] = useState('');

  // "15 — Table Settings / H - Table List Style": Standard cards vs a compact
  // Regular list.
  const regularStyle = isOn('table_list_style') && choice('table_list_style_choice') === 'Regular';
  // "J - Table Book Without KOT": book/free a table without firing items.
  const bookWithoutKot = isOn('table_book_without_kot');

  const addTable = () => {
    rest.addTable(newTable);
    setNewTable('');
  };

  // Take a table's lock before opening it, so two terminals can't bill the same
  // table at once. If another terminal holds it, say so instead of opening.
  const openTable = (t: string) => {
    const holder = rest.lockHolder(t);
    if (holder) {
      window.alert(`${t} is being edited on ${holder.deviceName || 'another terminal'}.`);
      return;
    }
    if (!rest.acquireLock(t)) {
      window.alert(`${t} was just opened on another terminal.`);
      return;
    }
    setSelected(t);
  };

  useEffect(() => {
    if (!selected) return;
    const id = setInterval(() => rest.refreshLock(selected), LOCK_HEARTBEAT_MS);
    return () => {
      clearInterval(id);
      rest.releaseLock(selected);
    };
  }, [selected]);

  return (
    <div className="section-page rest-page">
      <div className="page-header">
        <h1>TABLES</h1>
      </div>

      <div className="tbl-add">
        <input
          placeholder="Add table (e.g. T13 / Garden-2)"
          value={newTable}
          onChange={(e) => setNewTable(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTable()}
        />
        <button className="btn-teal" style={{ padding: '8px 16px', borderRadius: 6 }} onClick={addTable}>
          + Add Table
        </button>
      </div>

      <div className={`tbl-grid ${regularStyle ? 'regular' : ''}`}>
        {rest.tables.map((t) => {
          const order = rest.runningOrder(t);
          const occupied = !!order;
          const booked = occupied && order!.items.length === 0;
          const total = order?.items.reduce((s, i) => s + i.total, 0) ?? 0;
          const qty = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0;
          const holder = rest.lockHolder(t);
          return (
            <button
              key={t}
              className={`tbl-card ${occupied ? 'occupied' : 'free'}`}
              onClick={() => openTable(t)}
            >
              {!occupied && (
                <span
                  className="tbl-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    rest.removeTable(t);
                  }}
                  title="Remove table"
                >
                  ×
                </span>
              )}
              <span className="tbl-name">{t}</span>
              <span className="tbl-status">{occupied ? (booked ? 'Booked' : 'Occupied') : 'Free'}</span>
              {occupied && !booked && (
                <span className="tbl-meta">
                  {qty} item{qty === 1 ? '' : 's'} · {formatMoney(total)}
                </span>
              )}
              {holder && (
                <span className="tbl-lock" title={`Editing on ${holder.deviceName || 'another terminal'}`}>
                  🔒 {holder.deviceName || 'in use'}
                </span>
              )}
              {bookWithoutKot && !occupied && (
                <span
                  className="tbl-book"
                  onClick={(e) => {
                    e.stopPropagation();
                    rest.bookTable(t);
                  }}
                >
                  Book
                </span>
              )}
              {bookWithoutKot && booked && (
                <span
                  className="tbl-book"
                  onClick={(e) => {
                    e.stopPropagation();
                    rest.unbookTable(t);
                  }}
                >
                  Unbook
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selected && (
        <OrderPanel
          tableName={selected}
          rest={rest}
          mode="cashier"
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default Tables;
